import { z } from 'zod'
import { RoleSchema } from './enums.ts'
import { GroupRefSchema, GroupWithSizeSchema } from './groups.ts'

/**
 * The people plane (PRD-21).
 *
 * Two rules shape every schema here.
 *
 * **`user` is not assignable.** It is the floor every authenticated principal stands on, so
 * granting it is a no-op and denying it is a lie. The database says the same thing with a CHECK
 * constraint; this is the same rule where the client can see it.
 *
 * **Nothing carries prose.** The API answers *which* role, *which* provenance, *which* reason
 * code; the interface owns every sentence, in a catalog, per AGM-124. The one exception is a
 * `reason` an administrator typed, which is their words and is relayed verbatim.
 */

export const AssignableRoleSchema = z.enum(['auditor', 'operator', 'admin'])
export type AssignableRole = z.infer<typeof AssignableRoleSchema>

export const AccountStatusSchema = z.enum(['active', 'deactivated'])
export type AccountStatus = z.infer<typeof AccountStatusSchema>

/**
 * Where a role came from, which is what decides whether the next action is possible here at all.
 *
 * - `floor` — everybody who signs in has it.
 * - `directory` — a claim in the sign-in token parsed as this role.
 * - `configuration` — their email is in `adminEmails` in this deployment's AUTH_PROVIDERS_JSON.
 *   Not revocable from inside the product, and the interface says so rather than offering a
 *   control that cannot work.
 * - `grant` — granted here, by a person, with a reason.
 *
 * Groups never confer a role (PRD-26 D-24): the group→role rules that did are gone, and a role is
 * always one of these four, per person.
 */
export const RoleProvenanceSchema = z.enum(['floor', 'directory', 'configuration', 'grant'])
export type RoleProvenance = z.infer<typeof RoleProvenanceSchema>

export const RoleAssignmentSchema = z.object({
  role: RoleSchema,
  provenance: RoleProvenanceSchema,
  /** The provider slug for `configuration`, null otherwise. */
  detail: z.string().nullable(),
  grantId: z.uuid().nullable(),
  grantedByName: z.string().nullable(),
  grantedAt: z.iso.datetime().nullable(),
  reason: z.string(),
  expiresAt: z.iso.datetime().nullable(),
  /** A grant awaiting a second administrator confers nothing until it is approved. */
  pendingApproval: z.boolean(),
  /**
   * Whether this source is what the account is ACTUALLY reaching the role through.
   *
   * False for a grant still waiting on a second administrator, and for anything a live `deny` is
   * currently subtracting. The profile lists those — an approval control needs something to
   * render, and "why can this person not do X" is answered by seeing the grant underneath the
   * override — but anything reporting the effective set (the row's chips, the export, the
   * before/after on an audit row) must count only what is true.
   */
  effective: z.boolean()
})
export type RoleAssignment = z.infer<typeof RoleAssignmentSchema>

/** A role the platform is subtracting. `stillSent` is false once the directory stops sending it. */
export const RoleDenialSchema = z.object({
  role: RoleSchema,
  grantId: z.uuid(),
  grantedByName: z.string().nullable(),
  grantedAt: z.iso.datetime(),
  reason: z.string(),
  stillSent: z.boolean()
})
export type RoleDenial = z.infer<typeof RoleDenialSchema>

export const PersonSummarySchema = z.object({
  id: z.uuid(),
  email: z.email(),
  displayName: z.string(),
  avatarInitials: z.string(),
  providerSlug: z.string(),
  status: AccountStatusSchema,
  roles: z.array(RoleAssignmentSchema),
  /** The groups they are a member of, by name. */
  groups: z.array(GroupRefSchema),
  /** The groups they manage — which is not being in them (PRD-26 D-3). */
  managedGroups: z.array(GroupRefSchema),
  lastLoginAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  deactivatedAt: z.iso.datetime().nullable(),
  /** True for the reader's own row: the surface refuses to offer self-demotion, and says why. */
  isYou: z.boolean()
})
export type PersonSummary = z.infer<typeof PersonSummarySchema>

export const PersonPageSchema = z.object({
  items: z.array(PersonSummarySchema),
  nextCursor: z.string().nullable()
})
export type PersonPage = z.infer<typeof PersonPageSchema>

export const PeopleCountsSchema = z.object({
  active: z.number().int(),
  deactivated: z.number().int(),
  /** An array rather than a record: only active accounts are tallied, so most roles are absent,
      and the interface wants them in rank order rather than in key order. */
  byRole: z.array(z.object({ role: RoleSchema, count: z.number().int() }))
})
export type PeopleCounts = z.infer<typeof PeopleCountsSchema>

/**
 * A group as it bears on one person: how they relate to it, and what membership reaches.
 *
 * The counts are the point. Membership is read inside the retrieval predicate (ADR-0005, PRD-26
 * D-34), so it is not a label — it is the use of every knowledge base shared with the group and of
 * every agent whose audience names it. An administrator looking at a person should be able to see
 * that without leaving.
 */
export const PersonGroupSchema = z.object({
  group: GroupWithSizeSchema,
  isMember: z.boolean(),
  isManager: z.boolean(),
  /** Knowledge bases shared with the group. */
  knowledgeBaseCount: z.number().int().nonnegative(),
  /** Published agents whose audience names the group. */
  agentCount: z.number().int().nonnegative()
})
export type PersonGroup = z.infer<typeof PersonGroupSchema>

export const OwnedAgentSchema = z.object({
  agentId: z.string(),
  displayName: z.string(),
  state: z.string()
})

export const OwnedBaseSchema = z.object({ id: z.uuid(), name: z.string() })

/**
 * The three things that become somebody else's problem when this person leaves: what they own
 * personally. What their groups own stays with the groups.
 */
export const OwnershipInventorySchema = z.object({
  agents: z.array(OwnedAgentSchema),
  knowledgeBases: z.array(OwnedBaseSchema),
  conversationCount: z.number().int()
})
export type OwnershipInventory = z.infer<typeof OwnershipInventorySchema>

export const PersonSessionSchema = z.object({
  id: z.uuid(),
  createdAt: z.iso.datetime(),
  lastSeenAt: z.iso.datetime(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  current: z.boolean()
})
export type PersonSession = z.infer<typeof PersonSessionSchema>

export const AccessHistoryEntrySchema = z.object({
  id: z.uuid(),
  at: z.iso.datetime(),
  action: z.string(),
  actorLabel: z.string(),
  outcome: z.string(),
  detail: z.record(z.string(), z.unknown())
})
export type AccessHistoryEntry = z.infer<typeof AccessHistoryEntrySchema>

export const PersonDetailSchema = PersonSummarySchema.extend({
  subject: z.string(),
  locale: z.string(),
  timeZone: z.string(),
  deactivationReason: z.string(),
  deactivatedByName: z.string().nullable(),
  denials: z.array(RoleDenialSchema),
  groupDetail: z.array(PersonGroupSchema),
  owns: OwnershipInventorySchema,
  sessions: z.array(PersonSessionSchema),
  history: z.array(AccessHistoryEntrySchema)
})
export type PersonDetail = z.infer<typeof PersonDetailSchema>

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/**
 * `.strict()` on every request body in this file, deliberately.
 *
 * These routes sit next to the columns that decide what a person may do. A body carrying
 * `roles`, `status` or `groups` has to be a 400 and not a partially applied write, and the
 * cheapest way to guarantee that for every field nobody has thought of yet is to accept only
 * the fields named here.
 */
export const GrantRoleRequestSchema = z
  .object({
    role: AssignableRoleSchema,
    effect: z.enum(['allow', 'deny']).default('allow'),
    reason: z.string().trim().max(500).default(''),
    expiresAt: z.iso.datetime().nullish()
  })
  .strict()
export type GrantRoleRequest = z.infer<typeof GrantRoleRequestSchema>

export const RevokeRoleRequestSchema = z
  .object({
    effect: z.enum(['allow', 'deny']).default('allow'),
    reason: z.string().trim().max(500).default('')
  })
  .strict()
export type RevokeRoleRequest = z.infer<typeof RevokeRoleRequestSchema>

export const PrepareGrantRequestSchema = z
  .object({
    email: z.email().trim().toLowerCase(),
    role: AssignableRoleSchema,
    reason: z.string().trim().max(500).default(''),
    expiresAt: z.iso.datetime().nullish()
  })
  .strict()
export type PrepareGrantRequest = z.infer<typeof PrepareGrantRequestSchema>

export const PendingGrantSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  role: RoleSchema,
  reason: z.string(),
  grantedByName: z.string().nullable(),
  grantedAt: z.iso.datetime(),
  expiresAt: z.iso.datetime().nullable(),
  claimExpiresAt: z.iso.datetime().nullable(),
  /** False when the address matches no domain any configured provider accepts: it can never be claimed. */
  claimable: z.boolean(),
  /** An administrator grant prepared while four-eyes is on still needs a second signature. */
  pendingApproval: z.boolean()
})
export type PendingGrant = z.infer<typeof PendingGrantSchema>

/**
 * Deactivation is refused without the subject's display name typed back, through the same
 * `confirm_name_required` refusal the knowledge plane uses — so the surface discovers the
 * requirement from the server rather than predicting it.
 */
export const DeactivateRequestSchema = z
  .object({
    reason: z.string().trim().max(500).default(''),
    confirmName: z.string().optional()
  })
  .strict()
export type DeactivateRequest = z.infer<typeof DeactivateRequestSchema>

export const ReasonOnlyRequestSchema = z.object({ reason: z.string().trim().max(500).default('') }).strict()

/** Every number here is a query, not a sentence: a consequence written in prose goes stale. */
export const DeactivationPreviewSchema = z.object({
  displayName: z.string(),
  sessions: z.number().int(),
  liveGrants: z.number().int(),
  owns: OwnershipInventorySchema,
  /**
   * Groups this person is the only active manager of. Nobody can add or remove their members
   * afterwards until an administrator appoints someone, and what they own needs an owner
   * (PRD-26 GRP-070, GRP-030).
   */
  groupsLeftWithoutManagers: z.array(GroupRefSchema),
  isLastAdmin: z.boolean()
})
export type DeactivationPreview = z.infer<typeof DeactivationPreviewSchema>

export const OffboardingRecordSchema = z.object({
  userId: z.uuid(),
  displayName: z.string(),
  email: z.string(),
  at: z.iso.datetime(),
  reason: z.string(),
  revokedSessions: z.number().int(),
  /** Grants are NOT revoked by deactivation: they confer nothing while the account is off, and
      keeping them is what makes reactivation restore the person to exactly what they had. */
  retainedGrants: z.number().int(),
  agentsNeedingOwner: z.array(OwnedAgentSchema),
  basesNeedingOwner: z.array(OwnedBaseSchema),
  groupsLeftWithoutManagers: z.array(GroupRefSchema),
  retainedConversations: z.number().int()
})
export type OffboardingRecord = z.infer<typeof OffboardingRecordSchema>

/**
 * Why a sign-in was refused, grouped by address.
 *
 * Read from the `auth.session.denied` audit events the login path already writes. A refused
 * sign-in leaves no user row — the insert is what was refused — so this is the only place such
 * a person is visible at all.
 */
export const SignInFailureSchema = z.object({
  email: z.string(),
  reason: z.string(),
  /**
   * Which refusal this was, as an identifier (PRD-27 D-4/D-6).
   *
   * `reason` is the English the refusal was thrown with, kept permanently: it is what an
   * operator reads and what distinguishes eleven identity-provider faults the three remedies
   * cannot. But an audit row is never re-rendered, so the sentence stored in 2026 is the
   * sentence forever — and a client that recovered the cause by reading those words would
   * lose it to a rewording and lose it entirely to a translation. So the cause is written
   * beside the prose at the moment of the refusal and read back from here.
   *
   * `null` for rows written before this landed, which cannot be retrofitted: the words in
   * them are a build's English, not an identifier. Those degrade to the neutral remedy.
   *
   * A plain string, not an enum of `MessageKey`, for the reason `ApiIssue.messageKey` is one:
   * a stored row may hold a key this build has not learned, and that must degrade rather
   * than fail to parse.
   */
  messageKey: z.string().nullable(),
  provider: z.string(),
  attempts: z.number().int(),
  lastAt: z.iso.datetime(),
  /** Set when the refusal was "this account is deactivated" and the account can be restored here. */
  userId: z.uuid().nullable()
})
export type SignInFailure = z.infer<typeof SignInFailureSchema>

/** Lives in `platform_settings` under the key `access`. */
export const AccessSettingsSchema = z
  .object({
    /**
     * Four-eyes on `admin` grants. Off by default, and that is a decision rather than an
     * oversight: a deployment with two administrators and this on is one resignation away from
     * a platform nobody can administer.
     */
    secondAdminForAdminGrants: z.boolean().default(false),
    pendingGrantTtlDays: z.number().int().min(1).max(365).default(30),
    defaultGrantExpiryDays: z.number().int().min(1).max(3650).nullable().default(null)
  })
  .strict()
export type AccessSettings = z.infer<typeof AccessSettingsSchema>

export const ACCESS_SETTINGS_KEY = 'access'
